import { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { CheckCircle, Loader2, Phone, Mail, MapPin, ExternalLink } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import logo from "@/assets/complyhub-logo.svg";
import { trackSubscribe, trackFormStart, trackTrialClick, trackDemoClick } from "@/lib/analytics";

const Footer = () => {
  const location = useLocation();
  const isActive = (path: string) => location.pathname === path;

  const [email, setEmail] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubscribed, setIsSubscribed] = useState(false);
  const [error, setError] = useState("");
  const [hasStartedForm, setHasStartedForm] = useState(false);

  const handleManageCookies = () => {
    window.dispatchEvent(new CustomEvent("open-cookie-preferences"));
  };

  const handleEmailFocus = () => {
    if (!hasStartedForm) {
      setHasStartedForm(true);
      trackFormStart("footer_subscribe");
    }
  };

  const handleSubscribe = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");

    if (!email.trim() || !email.includes("@")) {
      setError("Please enter a valid email address");
      return;
    }

    setIsSubmitting(true);

    try {
      await new Promise(resolve => setTimeout(resolve, 800));
      trackSubscribe("footer", email);
      setIsSubscribed(true);
      setEmail("");
    } catch (err) {
      setError("Something went wrong. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  // Reusable classes for internal / external links
  const linkBase = "transition-colors hover:text-[#23C0DD]";
  const cls = (path: string) =>
    `${linkBase} ${isActive(path) ? "text-[#ED1878]" : "text-white"}`;
  const extCls = `${linkBase} text-white inline-flex items-center gap-1`;
  const ExtIcon = () => (
    <ExternalLink className="w-3 h-3 opacity-60" aria-hidden="true" />
  );

  return (
    <footer
      className="w-full pb-20 md:pb-24"
      style={{ background: "#44235F", color: "#FFFFFF" }}
    >
      {/* Top Reinforcement Strip */}
      <div className="border-b border-white/10">
        <div className="mx-auto max-w-7xl px-6 py-8 flex flex-col sm:flex-row items-center justify-between gap-4">
          <p className="text-sm text-white/80 text-center sm:text-left">
            Built for audit-ready RTOs. Aligned to 2025 Standards.
          </p>
          <div className="flex items-center gap-3">
            <Button size="sm" className="bg-ch-cyan hover:bg-ch-cyan/90 text-white" asChild>
              <a
                href="/trial-signup"
                onClick={() => trackTrialClick('footer')}
              >
                Start Free Trial
              </a>
            </Button>
            <Button
              size="sm"
              variant="outline"
              className="border-ch-cyan/60 text-ch-cyan hover:bg-ch-cyan/10"
              asChild
            >
              <Link to="/demo#book" onClick={() => trackDemoClick('footer')}>
                Book a Demo
              </Link>
            </Button>
          </div>
        </div>
      </div>

      {/* Main Footer Columns */}
      <div className="mx-auto max-w-7xl px-6 py-12">
        <div className="grid gap-10 md:grid-cols-4">
          {/* Column 1 – Product */}
          <div>
            <div className="text-sm font-semibold text-ch-fuchsia">Product</div>
            <ul className="mt-4 space-y-2 text-sm">
              <li><Link className={cls("/product")} to="/product">Product Overview</Link></li>
              <li><Link className={cls("/compliance")} to="/compliance">Standards Alignment</Link></li>
              <li><Link className={cls("/auditors")} to="/auditors">Audit Readiness</Link></li>
              <li><Link className={cls("/pricing")} to="/pricing">Pricing</Link></li>
              <li>
                <Link
                  className={cls("/trial-signup")}
                  to="/trial-signup"
                  onClick={() => trackTrialClick('footer_link')}
                >
                  Start Free Trial
                </Link>
              </li>
            </ul>
          </div>

          {/* Column 2 – Resources */}
          <div>
            <div className="text-sm font-semibold text-ch-fuchsia">Resources</div>
            <ul className="mt-4 space-y-2 text-sm">
              <li><Link className={cls("/resources")} to="/resources">Compliance Guides</Link></li>
              <li><Link className={cls("/demo-library")} to="/demo-library">Demo Library</Link></li>
              <li><Link className={cls("/blog")} to="/blog">Blog</Link></li>
              <li><Link className={cls("/compliance-scorecard")} to="/compliance-scorecard">Compliance Scorecard</Link></li>
              <li><Link className={cls("/faq")} to="/faq">FAQs</Link></li>
              <li><Link className={cls("/podcast")} to="/podcast">Podcast</Link></li>
            </ul>
          </div>

          {/* Column 3 – Company */}
          <div>
            <div className="text-sm font-semibold text-ch-fuchsia">Company</div>
            <ul className="mt-4 space-y-2 text-sm">
              <li><Link className={cls("/contact")} to="/contact">Contact</Link></li>
              <li><Link className={cls("/executive-summary")} to="/executive-summary">For Governing Persons</Link></li>
              <li>
                <a
                  className={extCls}
                  href="https://demo.complyhub.ai"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Live Demo Schedule <ExtIcon />
                </a>
              </li>
            </ul>

            {/* Contact details */}
            <div className="mt-6 space-y-2 text-sm text-white/85">
              <a href="tel:1300729455" className="flex items-center gap-2 hover:text-[#23C0DD] transition-colors">
                <Phone className="w-3.5 h-3.5" />
                1300 729 455
              </a>
              <a href="/contact" className="flex items-center gap-2 hover:text-[#23C0DD] transition-colors">
                <Mail className="w-3.5 h-3.5" />
                Contact Us
              </a>
              <div className="flex items-center gap-2">
                <MapPin className="w-3.5 h-3.5" />
                Australia
              </div>
            </div>
          </div>

          {/* Column 4 – Legal & Governance */}
          <div>
            <div className="text-sm font-semibold text-ch-fuchsia">Legal &amp; Governance</div>
            <ul className="mt-4 space-y-2 text-sm">
              <li><Link className={cls("/privacy-policy")} to="/privacy-policy">Privacy Policy</Link></li>
              <li><Link className={cls("/terms-of-service")} to="/terms-of-service">Terms of Service</Link></li>
              <li><Link className={cls("/cookie-policy")} to="/cookie-policy">Cookie Policy</Link></li>
              <li><Link className={cls("/data-processing-addendum")} to="/data-processing-addendum">Data Processing Addendum</Link></li>
              <li><Link className={cls("/acceptable-use-policy")} to="/acceptable-use-policy">Acceptable Use Policy</Link></li>
              <li>
                <button
                  type="button"
                  className={`${linkBase} text-white`}
                  onClick={handleManageCookies}
                >
                  Manage Cookies
                </button>
              </li>
            </ul>

            {/* Newsletter */}
            <div className="mt-6">
              <p className="text-sm text-white/80 mb-3">
                Standards updates &amp; compliance insights.
              </p>
              {isSubscribed ? (
                <div className="flex items-center gap-2 text-sm text-[#23C0DD]">
                  <CheckCircle className="w-4 h-4" />
                  <span>Subscribed!</span>
                </div>
              ) : (
                <form onSubmit={handleSubscribe} className="space-y-2">
                  <div className="flex gap-2">
                    <Input
                      type="email"
                      aria-label="Email address"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      onFocus={handleEmailFocus}
                      placeholder="Your email"
                      className="flex-1 bg-white/10 border-white/20 text-white placeholder:text-white/50 h-10"
                      disabled={isSubmitting}
                    />
                    <Button
                      type="submit"
                      size="sm"
                      disabled={isSubmitting}
                      aria-label="Subscribe to newsletter"
                      className="bg-ch-cyan hover:bg-ch-cyan/90 text-white h-10 px-4"
                    >
                      {isSubmitting ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        "Subscribe"
                      )}
                    </Button>
                  </div>
                  {error && <p className="text-xs text-[#ED1878]">{error}</p>}
                  <p className="text-xs text-white/60">No spam. Unsubscribe anytime.</p>
                </form>
              )}
            </div>
          </div>
        </div>

        {/* Compliance Credibility Block */}
        <div className="mt-10 border-t border-white/10 pt-6">
          <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
            <div className="flex items-start gap-4">
              <Link to="/">
                <img
                  src={logo}
                  alt="ComplyHub - RTO Compliance Management Software"
                  className="h-8 w-auto brightness-0 invert"
                  width={80}
                  height={32}
                  loading="lazy"
                />
              </Link>
              <div className="max-w-md">
                <p
                  className="italic mb-1"
                  style={{ color: "#DFD8E8", fontSize: 14 }}
                >
                  We make Compliance Simple!
                </p>
                <p className="text-xs text-white/70">
                  ComplyHub supports RTOs in preparing for audits under the 2025 Standards framework. Powered by Vivacity Coaching &amp; Consulting.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Copyright Strip */}
        <div
          className="mt-6 pt-6"
          style={{ borderTop: "1px solid rgba(255,255,255,0.12)" }}
        >
          <div className="flex flex-col gap-2 md:flex-row md:items-center md:justify-between">
            <div className="text-xs" style={{ color: "rgba(255,255,255,0.6)" }}>
              © {new Date().getFullYear()} ComplyHub.ai Pty Ltd. All rights reserved. ABN: 40 140 059 016
            </div>
            <div className="text-xs" style={{ color: "rgba(255,255,255,0.6)" }}>
              Proudly Australian-owned &amp; operated.
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
